import React, { useEffect, useState } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Box, Button, Grid, makeStyles, TextField, MenuItem } from "@material-ui/core/";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import DnerhsApi, { CancelToken } from "../../../api/DnerhsApi";
import { useParams } from "react-router-dom";
import history from "../../../utils/History";
import Alert from "../../../components/Alert";
import ResponseUtils from "../../../utils/ResponseUtils";
import useAuth from "../../../hooks/Auth";
import ListIcon from '@material-ui/icons/List';
import VisibilityIcon from "@material-ui/icons/Visibility";

const formatFecha = (valor) => {
  if (!valor) return "";
  try {
    const d = new Date(valor);
    if (isNaN(d.getTime())) return valor;
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (e) {
    return valor;
  }
};

const ESTADOS_OPCIONES = [
  { value: "", label: "Todos" },
  { value: "Práctica aprobada por DNERHS", label: "Aprobada" },
  { value: "Práctica con modificaciones aprobadas por DNERHS", label: "Con modificaciones aprobadas" },
  { value: "Práctica cronograma finalizada", label: "Cronograma finalizado" },
];

const useStyles = makeStyles((theme) => ({
  title: {
    margin: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(),
  },
  filterBar: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    background: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const List = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [rows, setRows] = useState([]);
  const { hasRole } = useAuth();
  const params = useParams();

  const [filterEstado, setFilterEstado] = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");

  useEffect(() => {
    loadData({ page: 0, rowsPerPage, estado: "", fechaDesde: "", fechaHasta: "" });
  }, []);

  const buildUrl = (page, rowsPerPage, estado, fechaDesde, fechaHasta) => {
    let url = `/convenios/${params.convenioId}/practicas/aprobadas/page/?page=${page}&pageSize=${rowsPerPage}`;
    if (estado) url += `&estado=${encodeURIComponent(estado)}`;
    if (fechaDesde) url += `&fechaDesde=${encodeURIComponent(fechaDesde)}`;
    if (fechaHasta) url += `&fechaHasta=${encodeURIComponent(fechaHasta)}`;
    return url;
  };

  const loadData = async ({ page = 0, rowsPerPage = 10, estado = "", fechaDesde = "", fechaHasta = "" }) => {
    let source = CancelToken.source();
    try {
      const url = buildUrl(page, rowsPerPage, estado, fechaDesde, fechaHasta);
      const response = await DnerhsApi(url, { cancelToken: source.token });
      const { content, totalElements } = response.data;
      setRows(content);
      setTotalElements(totalElements);
    } catch (error) {
      console.log(error);
      if (error.response) {
        showServerError(error);
      }
    }
  };

  const handleSearch = () => {
    setPage(0);
    setRows([]);
    loadData({ page: 0, rowsPerPage, estado: filterEstado, fechaDesde: filterFechaDesde, fechaHasta: filterFechaHasta });
  };

  const handleClearFilters = () => {
    setFilterEstado("");
    setFilterFechaDesde("");
    setFilterFechaHasta("");
    setPage(0);
    setRows([]);
    loadData({ page: 0, rowsPerPage, estado: "", fechaDesde: "", fechaHasta: "" });
  };

  const handleChangePage = async (event, newPage) => {
    setRows([]);
    setPage(newPage);
    await loadData({ page: newPage, rowsPerPage, estado: filterEstado, fechaDesde: filterFechaDesde, fechaHasta: filterFechaHasta });
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setRows([]);
    await loadData({ page: 0, rowsPerPage: newRowsPerPage, estado: filterEstado, fechaDesde: filterFechaDesde, fechaHasta: filterFechaHasta });
  };

  const showServerError = (error) => {
    let message = "Ocurrió un error inesperado, por favor vuelva a reintentar";
    if (error.response) {
      message = ResponseUtils.getMessageError(error.response);
    }
    Alert.show({ message, type: "error" });
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper>
          <Box p={1}>
            <Typography variant="h5" color="primary" align="center" className={classes.title}>
              Prácticas
            </Typography>

            {/* Barra de filtros */}
            <Box className={classes.filterBar}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="Estado"
                    value={filterEstado}
                    onChange={(e) => setFilterEstado(e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                  >
                    {ESTADOS_OPCIONES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Fecha inicio desde"
                    type="date"
                    value={filterFechaDesde}
                    onChange={(e) => setFilterFechaDesde(e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Fecha fin hasta"
                    type="date"
                    value={filterFechaHasta}
                    onChange={(e) => setFilterFechaHasta(e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Button variant="contained" color="primary" size="small" onClick={handleSearch}>
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="outlined" size="small" onClick={handleClearFilters}>
                        Limpiar
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Carrera</TableCell>
                  <TableCell align="left">Materia</TableCell>
                  <TableCell align="left">Curso</TableCell>
                  <TableCell align="left">Semestre</TableCell>
                  <TableCell align="left">Fecha Inicio</TableCell>
                  <TableCell align="left">Fecha Fin</TableCell>
                  <TableCell align="left">Estado</TableCell>
                  <TableCell align="left">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell align="left">
                      {(row.convenioCarrera?.carreraPrograma.descripcion === "Postgrado"
                        ? "Postgrado - " + row.convenioCarrera.nombrePostgrado.descripcion
                        : row.convenioCarrera.carreraPrograma.descripcion)}
                    </TableCell>
                    <TableCell align="left">{row.materia}</TableCell>
                    <TableCell align="left">{row.curso}</TableCell>
                    <TableCell align="left">{row.semestre}</TableCell>
                    <TableCell align="left">{formatFecha(row.fechaDesde)}</TableCell>
                    <TableCell align="left">{formatFecha(row.fechaHasta)}</TableCell>
                    <TableCell align="left">{row.estado}</TableCell>
                    <TableCell align="left">
                      <Tooltip title="Confirmar Asistencias" aria-label="Ver">
                        <IconButton
                          size="small"
                          aria-label="confirmar asistencias"
                          color="primary"
                          onClick={() => history.push({
                            pathname: `/convenios/confirmacionesPracticas/confirmar/${row.id}`,
                            state: { readOnly: true }
                          })}
                        >
                          {(row.estado === "Práctica cronograma finalizada") ? <VisibilityIcon /> : <ListIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 15, 20]}
                    colSpan={8}
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { "aria-label": "Registros por página" },
                      native: false,
                    }}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    labelRowsPerPage="Registros por página"
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
            <Grid container direction="row" justifyContent="flex-end" alignItems="center">
              <Grid item className={classes.button}>
                <Button variant="contained" color="default" onClick={() => history.goBack()}>
                  Atrás
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default List;
